import { Component, computed, inject, OnInit, Signal } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AsyncPipe } from '@angular/common';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { AuthService } from '../services/auth.service';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HealthIndicatorComponent } from '../components/health-indicator.component';
import { HTTPTransactionLoadingComponent } from '../components/shared/http-transaction-loading/http-transaction-loading.component';
import { Role } from '../services/model';

interface NavItem {
  path: string;
  title: string;
  icon: string;
  roles?: Role[];
}

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    AsyncPipe,
    RouterLink,
    RouterLinkActive,
    MatSidenavModule,
    MatButtonModule,
    MatMenuModule,
    MatBadgeModule,
    RouterOutlet,
    MatCardModule,
    MatDialogModule,
    MatListModule,
    MatIconModule,
    MatTooltipModule,
    HealthIndicatorComponent,
    HTTPTransactionLoadingComponent,
  ],
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly authService = inject(AuthService);

  // Get current user data from OIDC
  currentUser$ = this.authService.getUserClaims();

  // Get authentication status
  isAuthenticated$ = this.authService.isAuthenticated$;

  // Check if Admin
  isAdmin$ = this.authService.hasRole(Role.Admin);

  private readonly allRoutes: NavItem[] = [
    {
      path: 'documents',
      title: 'Dokumente',
      icon: 'folder',
      roles: [Role.Admin, Role.Manager, Role.User, Role.Viewer],
    },
    {
      path: 'document-types',
      title: 'Dokumententypen',
      icon: 'category',
      roles: [Role.Admin, Role.Manager, Role.User, Role.Viewer],
    },
    {
      path: 'data-types',
      title: 'Datentypen',
      icon: 'data_object',
      roles: [Role.Admin, Role.Manager, Role.User, Role.Viewer],
    },
    {
      path: 'extraction-rules',
      title: 'Extraction',
      icon: 'filter_alt',
      roles: [Role.Admin, Role.Manager, Role.User, Role.Viewer],
    },
    {
      path: 'settings',
      title: 'Einstellungen',
      icon: 'settings',
      roles: [Role.Admin, Role.Manager, Role.User, Role.Viewer],
    },
    { path: 'team-management', title: 'Team Management', icon: 'group', roles: [Role.Admin] },
  ];

  visibleRoutes$: Observable<NavItem[]> = this.authService.getUserClaims().pipe(
    map((claims) => {
      if (!claims) return [];
      const roles = claims['cognito:groups'] || claims.roles || [];
      const userRoles = Array.isArray(roles) ? roles : [roles];

      return this.allRoutes.filter((route) => {
        if (!route.roles) return true;
        return route.roles.some((role) => userRoles.includes(role));
      });
    })
  );

  isHandset$: Observable<boolean> = this.breakpointObserver.observe([Breakpoints.HandsetPortrait]).pipe(
    map((result) => result.matches),
    shareReplay()
  );

  getIconForRoute(path: string): string {
    switch (path) {
      case 'dashboard':
        return 'dashboard';
      case 'documents':
        return 'description';
      case 'data-types':
        return 'data_object';
      case 'extraction-rules':
        return 'rule';
      case 'settings':
        return 'settings';
      default:
        return 'folder';
    }
  }

  openDialog() {
    console.log('Opening dialog');
  }

  logout() {
    this.authService.logout().subscribe({
      next: () => {
        // Optionally, navigate to login or home after logout
        window.location.href = '/';
      },
      error: (err) => {
        console.error('Logout failed:', err);
      },
    });
  }

  ngOnInit() {
    // Check authentication status on component init
    this.authService.getAuthenticationResult().subscribe((result) => {
      console.log('Authentication result:', result);
    });
  }
}
